(ns app.front.view.group.admin.bonds.config
  "Bond configuration UI for managing authority group bond access and bond templates"
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.multi-select :as ms]
            [app.front.ui.table :as ui-table]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.group.admin.attachment-type-config :as at-config]
            [app.front.view.group.admin.bonds.groups :as bond-groups]
            [app.front.view.group.admin.common :as common]
            [app.front.view.group.admin.core :as acore]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.data :as cd]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


;;; ---------------------------------------------------------------------------
;;; State management
;;; ---------------------------------------------------------------------------

(rf/reg-sub ::state
  :-> ::state)

(rf/reg-sub ::selected-template-id
  :<- [::state]
  :-> :selected-template-id)


(rf/reg-sub ::creating-new?
  :<- [::selected-template-id]
  (fn [selected-id _]
    (= selected-id :new)))

(defn- build-form-data
  "Builds form data by merging template entity with unsaved changes"
  [template-entity unsaved-changes template-id]
  (let [enabled? (not= :bond-template.state/disabled (:domain/state template-entity))
        base     (if (= template-id :new)
                   {:names {} :enabled? false :required-attachment-types #{}}
                   {:names                     (i18n-model/strings-map (:bond-template/names template-entity))
                    :enabled?                  enabled?
                    :required-attachment-types (set (or (:bond-template/required-attachment-types template-entity) #{}))})]
    (merge base unsaved-changes)))

(rf/reg-sub ::form-data
  (fn [[_ template-id]]
    [(rf/subscribe [::state])
     (rf/subscribe [::entities/entity-data (when (not= template-id :new) template-id)])])
  (fn [[state template] [_ template-id]]
    (build-form-data template (get-in state [:unsaved-changes template-id]) template-id)))

(rf/reg-sub ::has-unsaved-changes?
  :<- [::state]
  (fn [state [_ template-id]]
    (some? (get-in state [:unsaved-changes template-id]))))

(defn- validate-when [form-data key pred]
  (if (contains? form-data key)
    (when (pred (key form-data)) form-data)
    form-data))

(defn- valid-template-changes? [form-data]
  (-> form-data
      (validate-when :names #(some str-util/blank->nil (vals %)))))

(defn- valid-new-template? [form-data]
  (some str-util/blank->nil (vals (:names form-data))))

(rf/reg-sub ::form-valid?
  :<- [::state]
  (fn [state [_ template-id]]
    (let [unsaved  (get-in state [:unsaved-changes template-id])
          editing? (not= template-id :new)]
      (if editing?
        (valid-template-changes? unsaved)
        (valid-new-template? unsaved)))))

(rf/reg-sub ::bond-templates
  :<- [::entities/context]
  :<- [::gas/group-id]
  (fn [[ctx group-id] _]
    (->> (entities/find-entities ctx {:bond-template/group group-id
                                      :entity/type         :entity.type/bond-template})
         (filter #(not= :bond-template.state/deprecated (:domain/state %)))
         (sort-by :db/id >))))

(rf/reg-sub ::required-attachment-type-ids
  :<- [::bond-templates]
  (fn [templates _]
    (into #{} (mapcat #(entities/get-id % :bond-template/required-attachment-types) templates))))

(rf/reg-sub ::selected-bond-attachment-ids
  :<- [::gas/group]
  (fn [group _]
    (set (entities/get-id group :group/available-bond-attachments))))

(rf/reg-sub ::has-bond-admin-permission?
  :<- [::state]
  (fn [state _]
    (contains? (:permissions state) :permission.bond/admin)))

(rf/reg-sub ::available-bond-attachment-types
  :<- [::gas/attachment-types]
  :<- [::selected-bond-attachment-ids]
  :<- [:language]
  (fn [[attachment-types selected-ids lang] _]
    (->> attachment-types
         (filter #(contains? selected-ids (:db/id %)))
         (sort-by #(attachment-common/attachment-type-label % lang)))))

(rf/reg-sub ::domain-group-config
  :<- [::state]
  :-> :domain-config)

(rf/reg-sub ::loading?
  :<- [::state]
  :-> :loading?)

;;; ---------------------------------------------------------------------------
;;; Events
;;; ---------------------------------------------------------------------------

(rf/reg-event-db ::clear-loading
  (rf/path ::state)
  (fn [state _]
    (dissoc state :loading?)))

(rf/reg-event-fx ::init
  (rf/path ::state)
  (fn [_ [_ group-id]]
    {:db {:group-id group-id}
     :fx [[:dispatch [::fetch-bond-templates group-id]]
          [:dispatch [::fetch-domain-config group-id]]
          [:dispatch [::gas/fetch-attachment-types-in-use group-id]]]}))

(rf/reg-event-fx ::fetch-bond-templates
  (rf/path ::state)
  (fn [{:keys [db]} [_ group-id]]
    {:db           (assoc db :loading? true)
     :comm/command {:command          :bond-template/get-all
                    :method           :get
                    :data             {:group-id group-id}
                    :on-success       [::bond-templates-fetch.success]
                    :finally-dispatch [::clear-loading]}}))

(rf/reg-event-fx ::bond-templates-fetch.success
  (rf/path ::state)
  (fn [_ [_ status {:keys [entities]}]]
    {:fx [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]]]}))

(rf/reg-event-fx ::fetch-domain-config
  (rf/path ::state)
  (fn [{:keys [db]} [_ group-id]]
    {:db           (assoc db :loading? true)
     :comm/command {:command          :bond-config/get
                    :method           :get
                    :data             (util/mmap group-id)
                    :on-success       [::domain-config-fetch.success]
                    :finally-dispatch [::clear-loading]}}))

(rf/reg-event-fx ::domain-config-fetch.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status {:keys [config permissions entities]}]]
    {:db (-> db
             (assoc :permissions (set permissions))
             (assoc :domain-config config))
     :fx [[:dispatch [::entities/merge-entity-db status entities]]]}))

(rf/reg-event-fx ::update-domain-config
  (rf/path ::state)
  (fn [{:keys [db]} [_ attribute value]]
    (let [group-id (:group-id db)]
      {:db           (assoc-in db [:domain-config attribute] value)
       :comm/command {:command    :bond-config/update
                      :method     :post
                      :data       {:group-id  group-id
                                   :attribute attribute
                                   :value     value}
                      :on-success [::update-domain-config.success]
                      :on-error   [::update-domain-config.error]}})))

(rf/reg-event-fx ::update-domain-config.success
  (fn [_]
    {:dispatch [:notifications/add :toast :success :success :bond.config/update-success]}))

(rf/reg-event-fx ::update-domain-config.error
  (rf/path ::state)
  (fn [{:keys [db]} _]
    (let [group-id (:group-id db)]
      {:dispatch [[::fetch-domain-config group-id]
                  [:notifications/add :toast :error :error :bond.config/update-error]]})))


(rf/reg-event-fx ::save-bond-attachment-config
  (fn [_ [_ group-id attachment-type-ids]]
    {:fx [[:dispatch [:comm/command-monitored
                      {:command    :bond-config/update-attachment-types
                       :method     :post
                       :data       (util/mmap group-id attachment-type-ids)
                       :on-success [::save-bond-attachment-config.success group-id]
                       :on-error   [::save-bond-attachment-config.error group-id]}]]]}))

(rf/reg-event-fx ::save-bond-attachment-config.success
  (fn [_ [_ group-id status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [:notifications/add :toast :success :success :bond.config/save-success]
                  [::gas/fetch-attachment-types-in-use group-id]]}))

(rf/reg-event-fx ::save-bond-attachment-config.error
  (fn [_ [_ group-id _ _]]
    {:dispatch-n [[::gas/fetch-attachment-types-in-use group-id]
                  [:notifications/add :toast :error :error :bond.config/save-error]]}))

;;; ---------------------------------------------------------------------------
;;; Template management events
;;; ---------------------------------------------------------------------------

(rf/reg-event-db ::select-template
  (rf/path ::state)
  (fn [state [_ template-id]]
    (let [current-id (:selected-template-id state)]
      (if (= current-id template-id)
        (-> state
            (assoc :selected-template-id nil)
            (update :unsaved-changes dissoc template-id))
        (-> state
            (assoc :selected-template-id template-id)
            (update :unsaved-changes dissoc template-id))))))

(rf/reg-event-db ::add-new-template
  (rf/path ::state)
  (fn [state _]
    (-> state
        (assoc :selected-template-id :new)
        (update :unsaved-changes dissoc :new))))

(rf/reg-event-db ::set-form-data
  (rf/path ::state)
  (fn [state [_ template-id field value]]
    (assoc-in state [:unsaved-changes template-id field] value)))

(rf/reg-event-db ::cancel-changes
  (rf/path ::state)
  (fn [state [_ template-id]]
    (if (= template-id :new)
      (-> state
          (assoc :selected-template-id nil)
          (update :unsaved-changes dissoc :new))
      (update state :unsaved-changes dissoc template-id))))

(rf/reg-event-db ::clear-deleting-template
  (rf/path ::state)
  (fn [state _]
    (let [template-id (:deleting-template-id state)]
      (-> state
          (dissoc :deleting-template-id)
          (assoc :selected-template-id nil)
          (update :unsaved-changes dissoc template-id)))))

(rf/reg-event-fx ::submit-form
  (fn [{:keys [db]} [_ template-id]]
    (let [unsaved  (get-in db [::state :unsaved-changes template-id])
          editing? (not= template-id :new)]
      {:dispatch [(if editing? ::update-template ::create-template) template-id unsaved]})))

(rf/reg-event-fx ::create-template
  (rf/path ::state)
  (fn [{:keys [db]} [_ _template-id form-data]]
    (let [required-ids (:required-attachment-types form-data)
          enabled?     (:enabled? form-data)
          data         (cond-> {:bond-template/names (:names form-data)}
                         (seq required-ids)
                         (assoc :bond-template/required-attachment-types required-ids)

                         :always
                         (assoc :domain/state (if enabled?
                                                :bond-template.state/active
                                                :bond-template.state/disabled)))]
      {:db           (assoc db :loading? true)
       :comm/command {:command          :bond-template/create
                      :method           :post
                      :data             {:group-id (:group-id db)
                                         :data     data}
                      :on-success       [::template-create.success]
                      :finally-dispatch [::clear-loading]}})))

(rf/reg-event-fx ::template-create.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status body]]
    (let [new-template-id (->> body keys (filter number?) first)
          group-id        (:group-id db)]
      {:db (-> db
               (assoc :selected-template-id new-template-id)
               (update :unsaved-changes dissoc :new))
       :fx [[:dispatch [::entities/apply-changes status body]]
            [:dispatch [::gas/fetch-attachment-types-in-use group-id]]
            [:dispatch [:notifications/add :toast :success :success :bond-template/created]]]})))

(rf/reg-event-fx ::update-template
  (rf/path ::state)
  (fn [{:keys [db]} [_ template-id unsaved-changes]]
    (let [changes (cond-> {}
                    (:names unsaved-changes)
                    (assoc :bond-template/names (:names unsaved-changes))

                    (:required-attachment-types unsaved-changes)
                    (assoc :bond-template/required-attachment-types (:required-attachment-types unsaved-changes))

                    (contains? unsaved-changes :enabled?)
                    (assoc :domain/state (if (:enabled? unsaved-changes)
                                           :bond-template.state/active
                                           :bond-template.state/disabled)))]
      {:db           (assoc db :loading? true)
       :comm/command {:command          :bond-template/edit
                      :method           :post
                      :data             {:bond-template-id template-id
                                         :changes          changes}
                      :on-success       [::template-update.success template-id]
                      :finally-dispatch [::clear-loading]}})))

(rf/reg-event-fx ::template-update.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ template-id status body]]
    (let [group-id (:group-id db)]
      {:db (update db :unsaved-changes dissoc template-id)
       :fx [[:dispatch [::entities/apply-changes status body]]
            [:dispatch [::gas/fetch-attachment-types-in-use group-id]]
            [:dispatch [:notifications/add :toast :success :success :bond-template/updated]]]})))

(rf/reg-event-fx ::delete-template
  (rf/path ::state)
  (fn [{:keys [db]} [_ template-id]]
    {:db           (assoc db :deleting-template-id template-id)
     :comm/command {:command          :bond-template/delete
                    :method           :post
                    :data             {:bond-template-id template-id}
                    :on-success       [::template-delete.success]
                    :finally-dispatch [::clear-deleting-template]}}))

(rf/reg-event-fx ::template-delete.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status body]]
    (let [group-id (:group-id db)]
      {:fx [[:dispatch [::entities/apply-changes status body]]
            [:dispatch [::gas/fetch-attachment-types-in-use group-id]]
            [:dispatch [:notifications/add :toast :success :success :bond-template/deleted]]]})))

;;; ---------------------------------------------------------------------------
;;; Components
;;; ---------------------------------------------------------------------------

(defn- attachment-type-selector
  "Pill-based selector for required attachment types (matching admin config pattern)"
  [tr template-id form-data]
  (let [attachment-types (<sub [::available-bond-attachment-types])
        selected-ids     (:required-attachment-types form-data)
        selected-types   (filter #(contains? selected-ids (:db/id %)) attachment-types)
        lang             (<sub [:language])]
    [:div.mt-4
     [ui-config-pills/config-pills
      {:id                (str "bond-template-attachment-types-" template-id)
       :legend-text       (tr [:bond-template/required-attachment-types])
       :values            attachment-types
       :selected          selected-types
       :val->str          #(attachment-common/attachment-type-label % lang)
       :val->id           :db/id
       :empty-placeholder :bond-template/no-attachment-types
       :add-item          (fn [attachment-type]
                            (let [new-ids (conj selected-ids (:db/id attachment-type))]
                              (rf/dispatch-sync [::set-form-data template-id
                                                 :required-attachment-types new-ids])))
       :remove-item       (fn [attachment-type]
                            (let [new-ids (disj selected-ids (:db/id attachment-type))]
                              (rf/dispatch-sync [::set-form-data template-id
                                                 :required-attachment-types new-ids])))
       :tr                tr}]]))

(defn template-editor
  "Inline editor for a bond template"
  [tr template-id]
  (let [form-data    (<sub [::form-data template-id])
        has-changes? (<sub [::has-unsaved-changes? template-id])
        new?         (= template-id :new)
        saving?      (<sub [::loading?])
        form-valid?  (<sub [::form-valid? template-id])]
    [:div.p-4.bg-gray-50.border-t.space-y-4
     [ui-i18n/edit-language-selector
      {:allowed-locales-sub ::gas/allowed-locales}]

     [:div.space-y-3
      [:div.flex.items-center.gap-4
       [:div
        (cc/toggle {:id         (str "bond-template-enabled-" template-id)
                    :toggled    (:enabled? form-data)
                    :labelA     (tr [:bond-template/disabled])
                    :labelB     (tr [:bond-template/enabled])
                    :class-name "unlabeled-toggle"
                    :on-toggle  #(rf/dispatch-sync [::set-form-data template-id
                                                    :enabled? (not (:enabled? form-data))])})]]
      [:div
       [:label.cds--label.mb-0 {:for (str "bond-template-name-" template-id)}
        (tr [:bond-template/name])
        [:span.text-red-600.ml-0.5 "*"]]
       [ui-i18n/localized-string-edit
        {:id                  (str "bond-template-name-" template-id)
         :strings             (:names form-data)
         :required            true
         :hide-label          true
         :on-change           #(rf/dispatch-sync [::set-form-data template-id
                                                  :names %])
         :allowed-locales-sub ::gas/allowed-locales}]]]

     [attachment-type-selector tr template-id form-data]

     [:div.flex.justify-between.items-center.mt-6
      [:div.flex.gap-2
       (when-not new?
         [ui/modal-confirmation-button
          {:tr                  tr
           :id                  (str "delete-bond-template-" template-id)
           :on-yes-click        #(>evt [::delete-template template-id])
           :trigger-button-text (tr [:bond-template/delete])
           :trigger-button-opts {:kind :tertiary
                                 :icon i/trash
                                 :size :small}}
          (tr [:bond-template/confirm-delete])])]
      [:div.flex.gap-2
       [ui/button {:kind     :tertiary
                   :size     :small
                   :disabled (not (or has-changes? new?))
                   :on-click #(>evt [::cancel-changes template-id])}
        (tr [:cancel])]
       [ui/button {:kind     :primary
                   :size     :small
                   :disabled (or (not has-changes?) (not form-valid?) (boolean saving?))
                   :on-click #(>evt [::submit-form template-id])}
        (tr [:save])]]]]))

(defn bond-templates-list
  "Table listing all bond templates with inline editing"
  [tr]
  (let [templates     (<sub [::bond-templates])
        selected-id   (<sub [::selected-template-id])
        creating-new? (<sub [::creating-new?])
        lang          (<sub [:language])
        admin?        (<sub [::has-bond-admin-permission?])]
    (if (or (seq templates) creating-new?)
      [ui-table/modern-responsive-table
       {:id                "bond-templates-table"
        :items             (if creating-new? (into [{:db/id :new}] templates) (vec templates))
        :cols              [[:bond-template/name
                             (fn [template]
                               (if (= (:db/id template) :new)
                                 [:span.italic.text-gray-500 (tr [:bond-template/new])]
                                 (let [disabled? (= :bond-template.state/disabled (:domain/state template))]
                                   [:span.flex.items-center.gap-2
                                    {:class (when disabled? "text-gray-400")}
                                    (bond-model/template-name template lang)])))]]
        :headerless?       true
        :expanded-func     (fn [template] [template-editor tr (:db/id template)])
        :expanded-row-keys (when selected-id #{selected-id})
        :on-expand         (fn [template-id _expanded?]
                             (when admin? (>evt [::select-template template-id])))
        :tr                tr
        :empty-placeholder :bond-template/no-templates}]
      [:p.text-gray-500.italic (tr [:bond-template/no-templates])])))

(defn- templates-section-header
  "Section header with title and add button"
  [tr]
  (let [creating-new? (<sub [::creating-new?])
        admin?        (<sub [::has-bond-admin-permission?])]
    [:div.flex.flex-row.justify-between.items-center.-mt-4
     [:h2.m-0 (tr [:bond.config/templates])]
     (when admin?
       [ui/button {:kind     :primary
                   :size     :small
                   :icon     i/plus
                   :disabled (boolean creating-new?)
                   :on-click #(>evt [::add-new-template])}
        (tr [:bond-template/add])])]))

(defn- config-checkbox
  "Checkbox for a boolean domain config attribute"
  [tr admin? attribute config]
  (let [checked? (get config attribute false)]
    (cc/checkbox
      {:id         (name attribute)
       :label-text (tr [attribute])
       :checked    checked?
       :disabled   (not admin?)
       :on-change  #(>evt [::update-domain-config attribute (not checked?)])})))

(defn- required-field-checkbox
  "Checkbox for a single required field"
  [tr admin? field selected on-change]
  (let [field-labels {:bond/issuer-phone (tr [:bond/issuer-phone])
                      :bond/issuer-email (tr [:bond/issuer-email])}
        checked?     (contains? selected field)]
    (cc/checkbox
      {:id         (str "required-" (name field))
       :label-text (get field-labels field (name field))
       :checked    checked?
       :disabled   (not admin?)
       :on-change  #(on-change (if checked? (disj selected field) (conj selected field)))})))

(defn- domain-config-editor
  "Editor for domain-level bond configuration options"
  [tr]
  (let [config   (<sub [::domain-group-config])
        admin?   (<sub [::has-bond-admin-permission?])
        selected (or (:group/bond-required-fields config) #{})]
    [:div
     {:class cc/carbon-checkbox-no-margin-class}
     [:div.grid.grid-cols-1.md:grid-cols-3.gap-4
      (for [attribute (sort (disj bond-model/bond-group-config-keys :group/bond-required-fields))]
        ^{:key attribute}
        [config-checkbox tr admin? attribute config])]
     [:div.mt-6
      [:h4.pb-2 (tr [:bond.config/required-fields])]
      [:div.grid.grid-cols-1.md:grid-cols-3.gap-4
       (for [field (sort bond-model/valid-bond-required-fields)]
         ^{:key field}
         [required-field-checkbox tr admin? field selected
          #(>evt [::update-domain-config :group/bond-required-fields %])])]]]))

(defn authority-groups-selector [{:keys [tr group-id]}]
  (let [groups-with-bonds (<sub [::bond-groups/groups-with-bonds group-id])]
    [ms/multi-checkbox
     {:id         "groups-with-bonds-select"
      :label-text (tr [:bond.config/select-groups])
      :loading?   (<sub [:comm/command-processing? :group/toggle-authority-group-feature-from-domain])
      :tr         tr
      :values     (<sub [::bond-groups/authority-groups group-id])
      :selected   groups-with-bonds
      :disabled   (<sub [::bond-groups/disabled-groups group-id])
      :val->str   :group/name
      :on-change  (fn [new-groups-with-bonds]
                    (let [[remove-from add-for _]
                          (cd/diff (du/entity-id-set groups-with-bonds)
                                   (du/entity-id-set new-groups-with-bonds))]
                      (>evt [::bond-groups/toggle-bonds add-for remove-from])))}]))

(defn- bond-attachment-config [group-id attribute-value-map]
  (rf/dispatch
    [::save-bond-attachment-config
     group-id
     (-> attribute-value-map :group/available-bond-attachments set)]))

(defn- attachment-type-disabled? [required-type-ids {:keys [id]}]
  (contains? required-type-ids id))

(defn- bond-attachments-config-section []
  (let [tr                  (<sub [::gas/group-tr])
        group-id            (<sub [::gas/group-id])
        group               (<sub [::gas/group])
        loading?            (<sub [::acore/fetching-config? group-id :group/attachment-types])
        required-type-ids   (<sub [::required-attachment-type-ids])
        default-attachments (<sub [::selected-bond-attachment-ids])]
    [common/section {:title (tr [:bond.config/available-attachments])}
     [:p.text-sm.text-gray-600.mb-4 (tr [:bond.config/available-attachments-instruction])]
     [:p.text-sm.text-gray-600.mb-4 (tr [:bond.config/attachment-types-in-use-help])]
     (if loading?
       (cc/loading-spinner {:with-overlay false
                            :small        true})
       ;; Only render editor when group data is loaded (has the :group/available-bond-attachments)
       (when (contains? group :group/available-bond-attachments)
         (r/with-let [*selected-attachments (r/atom {group-id default-attachments})]
           [at-config/available-attachments-editor
            tr
            nil
            group-id
            {:save-fn                  bond-attachment-config
             :available-attachments-kw :group/available-bond-attachments
             :default-attachments      default-attachments
             :toggle-disabled?         (partial attachment-type-disabled? required-type-ids)
             :kind                     :attachment-type.kind/bond}
            *selected-attachments])))]))

(defn bonds-configuration
  "UI for configuring which authority groups have access to bonds"
  []
  (let [tr       (<sub [::gas/group-tr])
        group-id (<sub [::gas/group-id])]
    [common/admin-config-form

     [common/section {:title (tr [:bond.config/authority-groups])}
      [authority-groups-selector (util/mmap tr group-id)]]

     [common/section {:title (tr [:bond.config/configuration])}
      [domain-config-editor tr]]

     [common/section {}
      [templates-section-header tr]
      [bond-templates-list tr]]

     [common/section {}
      [bond-attachments-config-section]]]))
